import { intlLocale } from "@/lib/i18n"
import type { DayTotal } from "@/features/staff/reports/types"

/**
 * Bucketing for the sales series, and the arithmetic underneath it.
 *
 * Two rules govern this file, and they pull against each other:
 *
 *   1. **No figure is re-derived in the browser.** Every amount on `/reports`
 *      is the exact decimal string the server built from integer cents, and R1
 *      binds the period total to the sum of its Z reports TO THE CENTAVO. A
 *      figure that has been through an IEEE-754 double cannot be held to that.
 *   2. A period wider than a month has to be **rolled up** or the axis becomes
 *      unreadable, and a roll-up is a sum.
 *
 * The reconciliation is that a roll-up here never touches a float. Amounts are
 * parsed to INTEGER CENTS by string, summed as integers, and rendered back to a
 * decimal string. Every intermediate value stays well inside `2^53`, so the sum
 * is exact and the centavo identity survives — which is what rule 1 protects.
 * `Number` parsing of the decimal itself would not survive it: `0.1 + 0.2`.
 *
 * `value` — the bar height — is the same integer, in cents. Recharts scales it
 * into pixels and it is never rendered as text; that is the same licence
 * `barPercent` already takes to turn an amount into a CSS width.
 */

export type SeriesGranularity = "day" | "week" | "month"

/** Up to a month of service, one column per service day. */
export const DAY_BUCKET_LIMIT = 31
/** Past that and up to roughly six months, one column per week. */
export const WEEK_BUCKET_LIMIT = 186

/**
 * Which bucket the series is drawn in, from the number of SERVICE days it
 * carries — not calendar days. `by_day` emits a row only for a day that carried
 * sales, so a restaurant closed on Mondays reaches 31 columns in five weeks, not
 * in a month, and that is the honest count for deciding whether the axis fits.
 */
export function chooseGranularity(serviceDayCount: number): SeriesGranularity {
  if (serviceDayCount <= DAY_BUCKET_LIMIT) return "day"
  if (serviceDayCount <= WEEK_BUCKET_LIMIT) return "week"
  return "month"
}

const DECIMAL = /^(-)?(\d+)(?:\.(\d{1,2}))?$/

/**
 * An exact decimal string as integer cents.
 *
 * `Staff::ReportSerializer.money` renders through `BigDecimal#to_s("F")`, which
 * drops trailing zeros — 184500 cents arrive as `"1845.0"`, not `"1845.00"`. So
 * the fraction is one OR two digits, and both have to pad to hundredths.
 *
 * A string this regexp does not recognise cannot be summed exactly, and there is
 * no honest answer for it, so it contributes nothing rather than contributing a
 * rounded guess. In practice it never happens: the only producer is the
 * serializer.
 */
export function decimalToCents(value: string | null | undefined): number {
  if (typeof value !== "string") return 0

  const match = DECIMAL.exec(value.trim())
  if (!match) return 0

  const [, sign, whole, fraction = ""] = match
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"))

  return sign === "-" ? -cents : cents
}

/** Integer cents back to the two-decimal string every formatter here expects. */
export function centsToDecimal(cents: number): string {
  const rounded = Math.trunc(cents)
  const sign = rounded < 0 ? "-" : ""
  const absolute = Math.abs(rounded)

  return `${sign}${Math.floor(absolute / 100)}.${String(absolute % 100).padStart(2, "0")}`
}

export type SeriesBucket = {
  /** `YYYY-MM-DD`: the day, the week's Monday, or the month's first. */
  key: string
  /** Full label for the tooltip and the screen-reader table. */
  label: string
  /** Short label for the axis tick, where 375px is the budget. */
  tick: string
  /**
   * Exact. The server's own string at day granularity; an integer-cent sum
   * above it. `null` means the bucket holds no close AT ALL — an absence, which
   * is a different fact from a bucket that traded and sold nothing.
   */
  amount: string | null
  count: number
  /** The bar height, in integer cents. Scaled into pixels, never into text. */
  value: number | null
}

/**
 * Exported for `charts/forecast-series.ts`, which anchors a forecast point's
 * `YYYY-MM-DD` on the same UTC-midnight convention as an actual bucket, so the
 * two halves of the forecast chart share one date-parsing rule instead of the
 * forecast series growing a second, slightly different one.
 */
export function parseIsoDate(iso: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!match) return null

  // UTC throughout. These are bucket labels the server already resolved on the
  // restaurant's wall clock, not instants; parsing them in the browser's zone
  // would file a Saturday under Friday in every zone Menny operates in.
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

function toIsoDate(date: Date): string {
  return date.toISOString().slice(0, 10)
}

/** The ISO week's Monday, so a forecast week and a historical week share a key. */
function startOfWeek(date: Date): Date {
  const weekday = (date.getUTCDay() + 6) % 7
  return new Date(date.getTime() - weekday * 86_400_000)
}

function startOfMonth(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1))
}

function bucketStart(date: Date, granularity: SeriesGranularity): Date {
  if (granularity === "week") return startOfWeek(date)
  if (granularity === "month") return startOfMonth(date)
  return date
}

function nextBucket(date: Date, granularity: SeriesGranularity): Date {
  if (granularity === "week") return new Date(date.getTime() + 7 * 86_400_000)
  if (granularity === "month") {
    return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 1))
  }
  return new Date(date.getTime() + 86_400_000)
}

/**
 * A bucket's label. `short` is the axis tick, where 375px is the whole budget;
 * the long form is what the tooltip and the screen-reader table read.
 *
 * `timeZone: "UTC"` everywhere, to match `parseIsoDate`: these dates were
 * anchored at UTC midnight precisely so no zone conversion can move them.
 */
/** Exported for the same reason as `parseIsoDate` above. */
export function formatBucket(date: Date, granularity: SeriesGranularity, short: boolean): string {
  const options = bucketFormatOptions(granularity, short)

  try {
    return new Intl.DateTimeFormat(intlLocale(), options).format(date)
  } catch {
    return toIsoDate(date)
  }
}

function bucketFormatOptions(
  granularity: SeriesGranularity,
  short: boolean
): Intl.DateTimeFormatOptions {
  if (granularity === "month") {
    return { month: short ? "short" : "long", year: "numeric", timeZone: "UTC" }
  }

  if (granularity === "week" || short) {
    return { day: "numeric", month: "short", timeZone: "UTC" }
  }

  return { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }
}

/**
 * `by_day` rows as the buckets the chart draws.
 *
 * At day granularity the axis IS the list of service days: a day the restaurant
 * did not open is not on it, because the axis measures service and a calendar
 * gap is not a bad day. Above day granularity the buckets are enumerated
 * CONTIGUOUSLY between the first and last one that carried a close, so a dead
 * week inside a quarter shows up as a hole in the axis rather than being
 * silently closed up. That hole carries `amount: null`, which reads as "sin
 * cierres" — never as zero, which would claim the restaurant opened and sold
 * nothing.
 */
export function bucketDaySeries(
  rows: DayTotal[],
  granularity: SeriesGranularity
): SeriesBucket[] {
  const parsed = rows
    .map((row) => ({ row, date: parseIsoDate(row.date) }))
    .filter((entry): entry is { row: DayTotal; date: Date } => entry.date !== null)
    .sort((a, b) => a.date.getTime() - b.date.getTime())

  if (parsed.length === 0) return []

  if (granularity === "day") {
    return parsed.map(({ row, date }) => ({
      key: row.date,
      label: formatBucket(date, "day", false),
      tick: formatBucket(date, "day", true),
      // The server's exact string, untouched. Nothing was added to reach it.
      amount: row.subtotal,
      count: row.count,
      value: decimalToCents(row.subtotal),
    }))
  }

  const totals = new Map<string, { cents: number; count: number }>()

  for (const { row, date } of parsed) {
    const key = toIsoDate(bucketStart(date, granularity))
    const bucket = totals.get(key) ?? { cents: 0, count: 0 }
    bucket.cents += decimalToCents(row.subtotal)
    bucket.count += row.count
    totals.set(key, bucket)
  }

  const first = bucketStart(parsed[0].date, granularity)
  const last = bucketStart(parsed[parsed.length - 1].date, granularity)
  const buckets: SeriesBucket[] = []

  for (let cursor = first; cursor <= last; cursor = nextBucket(cursor, granularity)) {
    const key = toIsoDate(cursor)
    const total = totals.get(key)

    buckets.push({
      key,
      label: formatBucket(cursor, granularity, false),
      tick: formatBucket(cursor, granularity, true),
      amount: total ? centsToDecimal(total.cents) : null,
      count: total ? total.count : 0,
      value: total ? total.cents : null,
    })
  }

  return buckets
}

/** The tallest bucket, as its own exact string. `null` for a series of absences. */
export function tallestBucket(buckets: SeriesBucket[]): SeriesBucket | null {
  return extremeBucket(buckets, (candidate, best) => candidate > best)
}

/** The shortest bucket that actually traded. An absence is not a slow day. */
export function shortestBucket(buckets: SeriesBucket[]): SeriesBucket | null {
  return extremeBucket(buckets, (candidate, best) => candidate < best)
}

function extremeBucket(
  buckets: SeriesBucket[],
  wins: (candidate: number, incumbent: number) => boolean
): SeriesBucket | null {
  let best: SeriesBucket | null = null

  for (const bucket of buckets) {
    if (bucket.amount === null) continue
    if (best === null || wins(decimalToCents(bucket.amount), decimalToCents(best.amount ?? "0"))) {
      best = bucket
    }
  }

  return best
}

/**
 * How many axis ticks to skip so the labels do not collide at 375px.
 *
 * Recharts takes the number of ticks to DROP between two it draws, so six
 * visible labels is `ceil(n / 6) - 1`. It always keeps the first.
 */
export function tickInterval(bucketCount: number, visible = 6): number {
  return Math.max(0, Math.ceil(bucketCount / visible) - 1)
}
